import React from 'react';
import {
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { COMPONENT_LABELS, getComponentSummary } from 'utils/metaFlowComponents';
import { sanitizeDataKey } from 'utils/metaFlowJson';
import DynamicApiResponseHint from './DynamicApiResponseHint';

const { Text } = Typography;
const { TextArea } = Input;

const TEXT_TYPES = [
  'text_large_heading',
  'text_small_heading',
  'text_body',
  'text_caption',
];

const INPUT_TYPES = ['text_input', 'text_area', 'text_email', 'text_phone'];

const SELECTION_TYPES = ['radio', 'checkbox', 'dropdown'];
const DYNAMIC_SELECTION_TYPES = ['radio', 'checkbox', 'dropdown'];
const DYNAMIC_LIST_TYPES = ['navigation_list'];

const DATA_MODE_OPTIONS = [
  { value: 'dynamic', label: 'From API (dynamic)' },
  { value: 'static', label: 'Fixed options' },
];

const FOOTER_ACTION_OPTIONS = [
  { value: 'data_exchange', label: 'Data exchange (call endpoint)' },
  { value: 'navigate', label: 'Navigate to screen' },
  { value: 'complete', label: 'Complete flow' },
];

const CLICK_ACTION_OPTIONS = [
  { value: 'data_exchange', label: 'Data exchange' },
  { value: 'navigate', label: 'Navigate' },
];

const moveListItem = (items, fromIndex, direction) => {
  const next = [...(items || [])];
  const toIndex = fromIndex + direction;
  if (toIndex < 0 || toIndex >= next.length) return items;
  [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
  return next;
};

const renderExampleItemEditor = (items, onChangeItems) => (
  <>
    {(items || []).map((item, itemIndex) => (
      <div
        key={itemIndex}
        style={{
          marginBottom: 10,
          padding: 10,
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          background: '#fff',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          <Input
            size="small"
            placeholder="ID (item_id)"
            value={item.id || ''}
            onChange={(e) => {
              const next = [...items];
              next[itemIndex] = { ...next[itemIndex], id: e.target.value };
              onChangeItems(next);
            }}
          />
          <Input
            size="small"
            placeholder="Title"
            value={item.title || ''}
            onChange={(e) => {
              const next = [...items];
              next[itemIndex] = { ...next[itemIndex], title: e.target.value };
              onChangeItems(next);
            }}
          />
          <Input
            size="small"
            placeholder="Description (optional)"
            value={item.description || ''}
            onChange={(e) => {
              const next = [...items];
              next[itemIndex] = { ...next[itemIndex], description: e.target.value };
              onChangeItems(next);
            }}
          />
          <Input
            size="small"
            placeholder="Image URL (optional)"
            value={item.image_src || ''}
            onChange={(e) => {
              const next = [...items];
              next[itemIndex] = { ...next[itemIndex], image_src: e.target.value };
              onChangeItems(next);
            }}
          />
          <Space size={0}>
            <Button
              type="text"
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={itemIndex === 0}
              onClick={() => onChangeItems(moveListItem(items, itemIndex, -1))}
            />
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={itemIndex === (items?.length || 0) - 1}
              onClick={() => onChangeItems(moveListItem(items, itemIndex, 1))}
            />
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onChangeItems(items.filter((_, i) => i !== itemIndex))}
            >
              Remove item
            </Button>
          </Space>
        </Space>
      </div>
    ))}
    <Button
      type="dashed"
      size="small"
      icon={<PlusOutlined />}
      onClick={() =>
        onChangeItems([
          ...(items || []),
          {
            id: `item_${(items?.length || 0) + 1}`,
            title: '',
            description: '',
            image_src: '',
            image_alt: '',
          },
        ])
      }
    >
      Add preview item
    </Button>
  </>
);

const ComponentCard = ({
  component,
  index,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  onTypeChange,
  lockType = false,
  canMoveUp = false,
  canMoveDown = false,
}) => {
  const typeOptions = Object.entries(COMPONENT_LABELS)
    .filter(([key]) => key !== 'button')
    .filter(([key]) => !lockType || key === component.type)
    .map(([value, label]) => ({ value, label }));

  const update = (patch) => onChange(index, { ...component, ...patch });

  const renderFields = () => {
    if (TEXT_TYPES.includes(component.type)) {
      const maxLen = component.max_length || 4096;
      return (
        <>
          <TextArea
            rows={component.type.includes('heading') ? 2 : 4}
            value={component.text || ''}
            maxLength={maxLen}
            onChange={(e) => update({ text: e.target.value })}
            placeholder={COMPONENT_LABELS[component.type]}
          />
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            {(component.text || '').length}/{maxLen}
          </Text>
        </>
      );
    }

    if (INPUT_TYPES.includes(component.type)) {
      return (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Field name
            </Text>
            <Input
              size="small"
              value={component.name || ''}
              onChange={(e) =>
                update({ name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })
              }
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              {component.type === 'text_area' ? 'Label' : 'Placeholder'}
            </Text>
            <Input
              size="small"
              value={component.type === 'text_area' ? component.label || '' : component.placeholder || ''}
              onChange={(e) =>
                update(
                  component.type === 'text_area'
                    ? { label: e.target.value }
                    : { placeholder: e.target.value }
                )
              }
            />
          </div>
          {component.type === 'text_input' || component.type === 'text_area' ? (
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Max length
              </Text>
              <InputNumber
                size="small"
                min={1}
                max={component.type === 'text_area' ? 600 : 120}
                value={component.max_length}
                onChange={(value) => update({ max_length: value })}
                style={{ width: '100%' }}
              />
            </div>
          ) : null}
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Required
            </Text>
            <Switch
              size="small"
              checked={component.required !== false}
              onChange={(checked) => update({ required: checked })}
            />
          </Space>
        </>
      );
    }

    if (DYNAMIC_LIST_TYPES.includes(component.type)) {
      return (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Field name
            </Text>
            <Input
              size="small"
              value={component.name || ''}
              onChange={(e) =>
                update({ name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })
              }
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Label
            </Text>
            <Input
              size="small"
              value={component.label || ''}
              onChange={(e) => update({ label: e.target.value })}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Data source
            </Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={component.data_mode || 'dynamic'}
              options={DATA_MODE_OPTIONS}
              onChange={(value) => update({ data_mode: value })}
            />
          </div>
          {component.data_mode === 'dynamic' ? (
            <>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  API data key (endpoint returns this array)
                </Text>
                <Input
                  size="small"
                  value={component.data_source_key || 'items'}
                  onChange={(e) =>
                    update({
                      data_source_key: sanitizeDataKey(e.target.value, 'items'),
                    })
                  }
                  placeholder="items"
                />
                <DynamicApiResponseHint
                  componentType={component.type}
                  dataSourceKey={sanitizeDataKey(component.data_source_key, 'items')}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Payload key on tap
                </Text>
                <Input
                  size="small"
                  value={component.payload_key || 'item_id'}
                  onChange={(e) =>
                    update({
                      payload_key: sanitizeDataKey(e.target.value, 'item_id'),
                    })
                  }
                  placeholder="item_id"
                />
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                  On the next screen endpoint, use{' '}
                  {`{${component.payload_key || 'item_id'}}`} in the URL path. The next screen API
                  must also return this key in data (e.g. event_id) so Complete can include it in
                  the webhook.
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                Preview items (for builder only; real data comes from API on INIT)
              </Text>
              {renderExampleItemEditor(component.example_items || [], (example_items) =>
                update({ example_items })
              )}
            </>
          ) : (
            <>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                Static list items
              </Text>
              {renderExampleItemEditor(component.items || [], (items) => update({ items }))}
            </>
          )}
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              On tap action
            </Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={component.click_action || 'data_exchange'}
              options={CLICK_ACTION_OPTIONS}
              onChange={(value) => update({ click_action: value })}
            />
          </div>
        </>
      );
    }

    if (SELECTION_TYPES.includes(component.type) || component.type === 'date_picker') {
      return (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Field name
            </Text>
            <Input
              size="small"
              value={component.name || ''}
              onChange={(e) =>
                update({ name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })
              }
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Label
            </Text>
            <Input
              size="small"
              value={component.label || ''}
              onChange={(e) => update({ label: e.target.value })}
            />
          </div>
          {DYNAMIC_SELECTION_TYPES.includes(component.type) ? (
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Data source
              </Text>
              <Select
                size="small"
                style={{ width: '100%' }}
                value={component.data_mode || 'static'}
                options={DATA_MODE_OPTIONS}
                onChange={(value) => update({ data_mode: value })}
              />
            </div>
          ) : null}
          {DYNAMIC_SELECTION_TYPES.includes(component.type) &&
            component.data_mode === 'dynamic' ? (
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                API data key
              </Text>
              <Input
                size="small"
                value={component.data_source_key || 'options'}
                onChange={(e) =>
                  update({
                    data_source_key: sanitizeDataKey(e.target.value, 'options'),
                  })
                }
                placeholder="options"
              />
              <DynamicApiResponseHint
                componentType={component.type}
                dataSourceKey={sanitizeDataKey(component.data_source_key, 'options')}
              />
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                Options load from endpoint on flow open (INIT).
              </Text>
            </div>
          ) : null}
          {SELECTION_TYPES.includes(component.type) &&
            !(
              DYNAMIC_SELECTION_TYPES.includes(component.type) &&
              component.data_mode === 'dynamic'
            ) ? (
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Options
              </Text>
              {(component.options || []).map((opt, optIndex) => (
                <Space key={optIndex} style={{ display: 'flex', marginBottom: 4 }} align="start">
                  <Input
                    size="small"
                    placeholder="Label"
                    value={opt.label || ''}
                    onChange={(e) => {
                      const options = [...(component.options || [])];
                      options[optIndex] = {
                        ...options[optIndex],
                        label: e.target.value,
                        value:
                          options[optIndex].value ||
                          e.target.value.toLowerCase().replace(/\s+/g, '_'),
                      };
                      update({ options });
                    }}
                  />
                  <Input
                    size="small"
                    placeholder="Value"
                    value={opt.value || ''}
                    onChange={(e) => {
                      const options = [...(component.options || [])];
                      options[optIndex] = { ...options[optIndex], value: e.target.value };
                      update({ options });
                    }}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={optIndex === 0}
                    onClick={() =>
                      update({ options: moveListItem(component.options, optIndex, -1) })
                    }
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={optIndex === (component.options || []).length - 1}
                    onClick={() =>
                      update({ options: moveListItem(component.options, optIndex, 1) })
                    }
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const options = (component.options || []).filter((_, i) => i !== optIndex);
                      update({ options });
                    }}
                  />
                </Space>
              ))}
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  const options = [
                    ...(component.options || []),
                    { value: `option_${(component.options?.length || 0) + 1}`, label: '' },
                  ];
                  update({ options });
                }}
              >
                Add option
              </Button>
            </div>
          ) : null}
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Required
            </Text>
            <Switch
              size="small"
              checked={component.required !== false}
              onChange={(checked) => update({ required: checked })}
            />
          </Space>
        </>
      );
    }

    if (component.type === 'opt_in') {
      return (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Field name
            </Text>
            <Input
              size="small"
              value={component.name || ''}
              onChange={(e) =>
                update({ name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })
              }
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Label
            </Text>
            <Input
              size="small"
              value={component.label || ''}
              onChange={(e) => update({ label: e.target.value })}
            />
          </div>
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Required
            </Text>
            <Switch
              size="small"
              checked={component.required !== false}
              onChange={(checked) => update({ required: checked })}
            />
          </Space>
        </>
      );
    }

    if (component.type === 'image') {
      return (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Image URL
            </Text>
            <Input
              size="small"
              value={component.src || ''}
              onChange={(e) => update({ src: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Alt text
            </Text>
            <Input
              size="small"
              value={component.alt || ''}
              onChange={(e) => update({ alt: e.target.value })}
            />
          </div>
        </>
      );
    }

    if (component.type === 'button') {
      return (
        <Row gutter={12}>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Button label
              </Text>
              <Input
                value={component.label || ''}
                onChange={(e) => update({ label: e.target.value })}
                placeholder="Button label"
                maxLength={20}
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Action
              </Text>
              <Select
                style={{ width: '100%' }}
                value={component.action || 'data_exchange'}
                options={FOOTER_ACTION_OPTIONS}
                onChange={(value) => update({ action: value })}
              />
            </div>
          </Col>
        </Row>
      );
    }

    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Unsupported component type: {component.type}
      </Text>
    );
  };

  const typeLabel = COMPONENT_LABELS[component.type] || component.type;
  const summary = getComponentSummary(component);

  return (
    <Card
      size="small"
      bordered
      style={{ marginBottom: 10, background: '#fafafa' }}
      bodyStyle={{ padding: '12px 14px' }}
      title={
        <div style={{ lineHeight: 1.3 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>{typeLabel}</div>
          <div
            style={{
              fontSize: 12,
              color: '#8c8c8c',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 260,
            }}
          >
            {summary}
          </div>
        </div>
      }
      extra={
        <Space size={0}>
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={!canMoveUp}
            onClick={() => onMoveUp?.(index)}
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={!canMoveDown}
            onClick={() => onMoveDown?.(index)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(index)}
          />
        </Space>
      }
    >
      {component.type !== 'button' ? (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            Type
          </Text>
          <Select
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
            value={component.type}
            options={typeOptions}
            disabled={lockType}
            onChange={(type) => onTypeChange(index, type)}
          />
        </div>
      ) : null}
      {renderFields()}
    </Card>
  );
};

export default ComponentCard;
